import { useEffect, useMemo, useState } from "react";
import { API_CACHE_TTL } from "@/utils/apiCache";
import { fetchAppleMusicEnrichment } from "@/utils/appleMusicEnrichment";
import type { AppleMusicEnrichment, AppleMusicResource } from "@/utils/appleMusicEnrichment";

interface UseAppleMusicReturn {
  data: AppleMusicResource | null;
  loading: boolean;
  error: string | null;
  url: string | null;
  previewUrl: string | null;
}

function useAppleMusic(
  artistName?: string | null,
  releaseName?: string | null,
  type: "song" | "album" = "song",
  dependencies: React.DependencyList = []
): UseAppleMusicReturn {
  const [data, setData] = useState<AppleMusicResource | null>(null);
  const [enrichment, setEnrichment] = useState<AppleMusicEnrichment | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!artistName || !releaseName) {
      setData(null);
      setEnrichment(null);
      return;
    }
    let cancelled = false;

    const loadAppleMusic = async () => {
      setLoading(true);
      setError(null);

      try {
        const enrichment = await fetchAppleMusicEnrichment({
          artistName,
          releaseName,
          lookupType: type,
        }, {
          ttlMs: API_CACHE_TTL.RELEASE_PAGE,
        });
        if (!cancelled) {
          setEnrichment(enrichment);
          setData(enrichment?.source === "apple-music" ? enrichment.raw as AppleMusicResource : null);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Apple Music lookup failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAppleMusic();
    return () => {
      cancelled = true;
    };
  }, [artistName, releaseName, type, ...dependencies]);

  const previewUrl = useMemo(() => enrichment?.previewUrl || data?.attributes?.previews?.find((preview) => preview.url)?.url || null, [data, enrichment]);

  return {
    data,
    loading,
    error,
    url: enrichment?.url || data?.attributes?.url || null,
    previewUrl,
  };
}

export default useAppleMusic;
